import axios from "axios";

// =============================================================================
// AUTHENTICATION SYSTEM ARCHITECTURE - 4 CORE COMPONENTS:
// 1. Dynamic Token Refresh - Proactive and reactive token management
// 2. Axios Interceptor - Global 401 handling and automatic retry
// 3. Request Queueing - Prevents race conditions during token refresh
// 4. Session Event System - Custom events for session state management
// =============================================================================

const autoRefreshInterval = 4 * 60 * 1000;
let autoRefreshIntervalId;
let isRefreshing = false;
let failedQueue = [];

// REQUEST QUEUEING: Process failed requests queue
// Manages concurrent requests during token refresh to prevent race conditions
const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

export function getToken() {
  const idTokenKey = findLocalStorageKey("idToken");
  const refreshTokenKey = findLocalStorageKey("refreshToken");
  const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : null;
  
  if (!idToken) {
    console.warn("No ID token found, redirecting to login");
    handleSessionExpired("No valid token found");
    return null;
  }

  try {
    const { exp } = decodeIdToken(idToken);
    const currentTime = Date.now() / 1000;
    const bufferTime = 60; // 1 minute buffer before expiration

    // DYNAMIC TOKEN REFRESH: Check token expiration and trigger refresh
    // If token is expired or will expire soon (60-second buffer)
    if (exp < currentTime + bufferTime) {
      console.log("Token expired or expiring soon, refreshing...");
      return refreshIdToken(refreshTokenKey, idTokenKey);
    }

    // DYNAMIC TOKEN REFRESH: Set up proactive auto-refresh interval
    // Automatically refreshes token every 4 minutes to prevent expiration
    if (!autoRefreshIntervalId) {
      autoRefreshIntervalId = setInterval(() => {
        console.log("Auto-refreshing token...");
        refreshIdToken(refreshTokenKey, idTokenKey);
      }, autoRefreshInterval);
    }

    return {
      headers: { Authorization: `Bearer ${idToken}` },
    };
  } catch (error) {
    console.error("Error processing token:", error);
    handleSessionExpired("Invalid token format");
    return null;
  }
}

function findLocalStorageKey(suffix) {
  return Object.keys(localStorage).find((key) => key.endsWith(suffix));
}

function decodeIdToken(idToken) {
  try {
    const base64Url = idToken.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch (error) {
    console.error("Failed to decode ID token:", error);
    throw new Error("Invalid token format");
  }
}

async function refreshIdToken(refreshTokenKey, idTokenKey) {
  // REQUEST QUEUEING: Prevent concurrent refresh operations
  // If already refreshing, queue the request instead of starting another refresh
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  try {
    isRefreshing = true;
    
    const currentRefreshToken = localStorage.getItem(refreshTokenKey);
    if (!currentRefreshToken) {
      // Show session expire popup instead of throwing error
      handleSessionExpired("No refresh token available - Please log in again");
      throw new Error("No refresh token available");
    }

    console.log("Refreshing ID token...");
    const newIdToken = await yourRefreshTokenLogic(currentRefreshToken);
    
    if (newIdToken) {
      localStorage.setItem(idTokenKey, newIdToken);
      console.log("Token refreshed successfully");
      
      // REQUEST QUEUEING: Process any queued requests with new token
      processQueue(null, {
        headers: { Authorization: `Bearer ${newIdToken}` },
      });
      
      return {
        headers: { Authorization: `Bearer ${newIdToken}` },
      };
    } else {
      throw new Error("Failed to get new token");
    }
    
  } catch (error) {
    console.error("Token refresh failed:", error);
    // REQUEST QUEUEING: Reject all queued requests on refresh failure
    processQueue(error, null);
    handleSessionExpired(error.message || "Token refresh failed");
    throw error;
  } finally {
    isRefreshing = false;
  }
}

async function yourRefreshTokenLogic(refreshToken) {
  const tokenEndpoint = `https://${process.env.REACT_APP_AWS_COGNITO_DOMAIN}.auth.${process.env.REACT_APP_AWS_COGNITO_REGION}.amazoncognito.com/oauth2/token`;
  const clientId = process.env.REACT_APP_AWS_COGNITO_CLIENT_ID;

  try {
    console.log("Making token refresh request...");
    const response = await axios.post(
      tokenEndpoint,
      `grant_type=refresh_token&refresh_token=${refreshToken}&client_id=${clientId}`,
      { 
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        timeout: 10000 // 10 second timeout
      }
    );

    if (response.data && response.data.id_token) {
      return response.data.id_token;
    } else {
      throw new Error("Invalid response from token endpoint");
    }
    
  } catch (error) {
    console.error("Token refresh API call failed:", error);
    
    // Handle different error scenarios by showing session logout popup
    let reason = "Network error during token refresh";
    
    if (error.response?.status === 400) {
      reason = "Invalid refresh token";
    } else if (error.response?.status === 401) {
      reason = "Refresh token expired";
    } else if (error.code === 'ECONNABORTED') {
      reason = "Token refresh timeout";
    } else if (error.message) {
      reason = error.message;
    }
    
    // Show session logout popup instead of throwing error
    handleSessionExpired(reason);
    throw new Error(reason); // Still throw for caller handling, but popup will show
  }
}

// SESSION EVENT SYSTEM: Centralized session expiration handler
// Dispatches custom events to notify UI components of session state changes
function handleSessionExpired(reason) {
  console.error("Session expired:", reason);
  
  // Clear all auth-related data
  const idTokenKey = findLocalStorageKey("idToken");
  const refreshTokenKey = findLocalStorageKey("refreshToken");
  const accessTokenKey = findLocalStorageKey("accessToken");
  
  if (idTokenKey) localStorage.removeItem(idTokenKey);
  if (refreshTokenKey) localStorage.removeItem(refreshTokenKey);
  if (accessTokenKey) localStorage.removeItem(accessTokenKey);
  
  // DYNAMIC TOKEN REFRESH: Clear auto-refresh interval on session expiration
  if (autoRefreshIntervalId) {
    clearInterval(autoRefreshIntervalId);
    autoRefreshIntervalId = null;
  }
  
  // SESSION EVENT SYSTEM: Dispatch custom session expired event
  // UI components can listen for this event to show popups or redirect users
  const sessionExpiredEvent = new CustomEvent('sessionExpired', {
    detail: { 
      error: reason,
      timestamp: new Date().toISOString()
    }
  });
  window.dispatchEvent(sessionExpiredEvent);
  
  // The user-session.js component will handle showing the popup
  // No direct redirect here - let the popup handle user interaction
}

// AXIOS INTERCEPTOR: Global 401 handling and automatic retry mechanism
// Automatically intercepts 401 responses and attempts token refresh + request retry
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // AXIOS INTERCEPTOR: Handle 401 unauthorized responses
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        console.log("Received 401, attempting token refresh...");
        // DYNAMIC TOKEN REFRESH: Attempt to get fresh token on 401 error
        const newToken = await getToken();
        
        if (newToken) {
          // AXIOS INTERCEPTOR: Retry original request with new token
          originalRequest.headers.Authorization = newToken.headers.Authorization;
          return axios(originalRequest);
        }
      } catch (refreshError) {
        console.error("Failed to refresh token on 401:", refreshError);
        // SESSION EVENT SYSTEM: Trigger session expiration on refresh failure
        handleSessionExpired("Authentication failed");
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);
